import React, { useMemo } from "react";
import t from "../../../../../../i18n";
import useVisibleSeries from "@ui/Highcharts/hooks/useVisibleSeries";
import PerformanceIndicatorSelector from "../../../PerformanceIndicatorSelector";
import ProjectAggregation from "../ProjectAggregationSelector";
import ReportingChart from "../../../ReportingChart";
import OrderingSelector from "./OrderingSelector";
import { PeriodComparisonSelector } from "./PeriodComparisonSelector";
import { useOptions } from "./useOptions";
import { useSeries } from "./useSeries";
import { useTranslations } from "../../../../../../api/useTranslations";
import type { PerformanceReporting } from "../../../../../../api/useReporting";
import { HIDE_ON_WIDGET_EXPORT } from "@ui/ExportWidgetButton";

function FleetInternalLossesComparison({ reporting }: { reporting: PerformanceReporting }) {
  const translations = useTranslations();
  const series = useSeries(reporting, reporting.range);

  const chartDefinition = useMemo(
    () => ({
      id: "FleetInternalLossesComparison",
      title: t("reporting.fleet_internal_gaps.title", translations),
      description: {
        title: t("reporting.chart.info.title"),
        description: t("reporting.fleet_internal_gaps.info.description", translations),
        options: [
          {
            value: "INTERNAL_GAPS_OPTION",
            label: t("reporting.internal_gaps", translations),
            description: t("reporting.internal_gaps.description"),
          },
          {
            value: "INTERNAL_GAPS_PERCENTAGE_OPTION",
            label: t("reporting.internal_gaps_percentage", translations),
            description: t("reporting.internal_gaps_percentage.description", translations),
          },
        ],
      },
    }),
    [translations],
  );

  const [visibleSeries] = useVisibleSeries(chartDefinition.id, series);
  const options = useOptions({ reporting, series: visibleSeries });

  return (
    <ReportingChart
      id={chartDefinition.id}
      reporting={reporting}
      title={chartDefinition.title}
      description={chartDefinition.description}
      series={series}
      options={options}
      leftOptions={
        <>
          <ProjectAggregation />
          <PerformanceIndicatorSelector reporting={reporting} />
          <OrderingSelector />
        </>
      }
      rightOptions={
        <>
          <PeriodComparisonSelector reporting={reporting} className={HIDE_ON_WIDGET_EXPORT} />
        </>
      }
    />
  );
}

export default React.memo(FleetInternalLossesComparison);
